/**
 * GlassSheet — iter86.95 One UI Phase 0 primitive.
 * ONE component, responsive anchoring only: bottom sheet on mobile,
 * floating top-right glass panel on desktop (lg:). Glassmorphic per
 * design guidelines: backdrop-blur, 1px white/10 border, no muddy blacks.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export const GlassSheet = ({ open, onClose, title, children, testId = "glass-sheet" }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    // Portal to <body>: ancestors with backdrop-filter (e.g. the glass
    // HeroStrip) become containing blocks for fixed positioning.
    return createPortal(
        <div className="fixed inset-0 z-50" data-testid={testId}>
            <div
                className="absolute inset-0 bg-black/60"
                onClick={onClose}
                data-testid={`${testId}-overlay`}
            />
            <div
                data-testid={`${testId}-panel`}
                className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-3xl glass border-t border-white/10 p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] animate-slide-up lg:inset-x-auto lg:right-6 lg:top-16 lg:bottom-auto lg:w-[400px] lg:max-h-[80dvh] lg:rounded-3xl lg:border"
            >
                <div className="lg:hidden mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
                <div className="flex items-center justify-between mb-2">
                    {title && (
                        <h3 className="font-display font-black text-lg uppercase tracking-tight">{title}</h3>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid={`${testId}-close`}
                        aria-label="Close"
                        className="ml-auto w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {children}
            </div>
        </div>,
        document.body
    );
};

export default GlassSheet;
